import { useCallback } from "react";

import useUser from "./useUser";
import { useAppDispatch, useAppSelector } from "@/redux/store/hooks";
import {
  openGuestLogin,
  openProfileGate,
  closeAuthGate,
  type AuthGateStep,
  type ProfileGateMode
} from "@/redux/features/profile-gate/slice";
import { resetGuestLogin } from "@/redux/features/user/slice";
import { clearAllErrors } from "@/redux/features/place-form/slice";
import { resetAnswers } from "@/redux/features/questions/slice";
import { hasGuestOptionalProfilePromptBeenShown } from "@/helpers/guest-profile-prompt-session";

interface ProfileGateOptions {
  reason: string;
  source: string;
  returnTo?: string;
}

const useAuthGate = () => {
  const dispatch = useAppDispatch();
  const {
    isGuestUser,
    isFullyRegistered,
    isRegisteredUserLoggedIn,
    isUnverfiedUserLoggedIn
  } = useUser();
  const { isOpen } = useAppSelector((state) => state.authGate);
  const guestUserId = useAppSelector(
    (state) => state.user.registeredUser.profile?.id
  );
  const isGuestLoginFeatureEnabled = useAppSelector(
    (state) => state.features.features.guestLogin?.enabled === true
  );
  const disableSignup = useAppSelector(
    (state) => state.settings.settings.disableSignup
  );
  const isGuestParticipationEnabled =
    isGuestLoginFeatureEnabled && !disableSignup;

  const openGuestLoginModal = (
    opts?: Record<string, unknown> & { initialStep?: AuthGateStep }
  ) => {
    if (!isGuestParticipationEnabled) return;
    const { initialStep, ...extraQueryParams } = opts ?? {};
    dispatch(
      openGuestLogin({
        initialStep,
        extraQueryParams:
          Object.keys(extraQueryParams).length > 0
            ? extraQueryParams
            : undefined
      })
    );
  };

  const closeGuestLoginModal = () => {
    dispatch(closeAuthGate());
    dispatch(resetAnswers());
    dispatch(clearAllErrors());
    dispatch(resetGuestLogin());
  };

  /**
   * Returns true if the user can proceed (fully registered).
   * Returns false and opens the profile gate modal if the user is a guest.
   */
  const requireProfileCompletion = (opts: ProfileGateOptions): boolean => {
    if (isFullyRegistered) return true;

    if (isGuestUser && !isGuestParticipationEnabled) {
      return true;
    }

    if (isGuestUser) {
      dispatch(
        openProfileGate({
          mode: "required" as ProfileGateMode,
          reason: opts.reason,
          source: opts.source,
          returnTo:
            opts.returnTo ?? window.location.pathname + window.location.search
        })
      );
      return false;
    }

    return false;
  };

  /**
   * Opens the profile gate modal in optional mode (dismissible).
   *
   * Guarded by localStorage after the guest clicks "Skip for now", shared across
   * tabs for the current guest login session. Cleared on logout so the prompt
   * can appear again after the guest signs in again. Until then, the optional
   * prompt may appear again on later contributions.
   *
   * Also no-ops when:
   *   - guest participation is disabled,
   *   - the user is already fully registered, or
   *   - the user is not a guest.
   */
  const suggestProfileCompletion = useCallback(
    (opts: Omit<ProfileGateOptions, "returnTo">): void => {
      if (!isGuestParticipationEnabled || isFullyRegistered || !isGuestUser) {
        return;
      }

      if (hasGuestOptionalProfilePromptBeenShown(guestUserId)) {
        return;
      }

      dispatch(
        openProfileGate({
          mode: "optional" as ProfileGateMode,
          reason: opts.reason,
          source: opts.source,
          returnTo: null
        })
      );
    },
    [
      dispatch,
      guestUserId,
      isFullyRegistered,
      isGuestUser,
      isGuestParticipationEnabled
    ]
  );

  return {
    isAuthGateOpen: isOpen,
    isGuestParticipationEnabled,
    isGuestUser,
    isFullyRegistered,
    isRegisteredUserLoggedIn,
    isUnverfiedUserLoggedIn,
    openGuestLoginModal,
    closeGuestLoginModal,
    requireProfileCompletion,
    suggestProfileCompletion
  };
};

export default useAuthGate;
